import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiFilter, FiSearch, FiRefreshCw } from "react-icons/fi";
import { ordersAPI } from "../../services/api";
import { OrderCard } from "../../components/OrderTracking";
import LoadingSpinner from "../../components/LoadingSpinner";

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background: #f8f9fa;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  color: #495057;
  font-weight: 500;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #495057;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #495057;
  font-size: 0.9rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const RefreshButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  color: #adb5bd;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
`;

const EmptyText = styled.p`
  color: #6c757d;
  font-size: 1rem;
  max-width: 400px;
  margin: 0 auto;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
  margin-bottom: 2rem;
  text-align: center;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  background: ${(props) => (props.active ? "#007bff" : "white")};
  color: ${(props) => (props.active ? "white" : "#495057")};
  border: 2px solid ${(props) => (props.active ? "#007bff" : "#e9ecef")};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.active ? "#0056b3" : "#f8f9fa")};
    border-color: ${(props) => (props.active ? "#0056b3" : "#007bff")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
        ...(filters.status && { status: filters.status }),
      };

      const response = await ordersAPI.getOrders(params);
      setOrders(response.data.data.orders);

      // Note: Backend doesn't return total count, so we'll estimate
      setPagination((prev) => ({
        ...prev,
        total:
          response.data.data.orders.length === pagination.limit
            ? pagination.page * pagination.limit + 1
            : (pagination.page - 1) * pagination.limit +
              response.data.data.orders.length,
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, filters.status]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // For now, we'll just refresh with current filters
    // In a real app, you'd implement search on the backend
    fetchOrders();
  };

  const filteredOrders = orders.filter((order) => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return (
        order.meal_name?.toLowerCase().includes(searchTerm) ||
        order.standalone_meal_name?.toLowerCase().includes(searchTerm) ||
        order.meal_plan_title?.toLowerCase().includes(searchTerm) ||
        order.business_name?.toLowerCase().includes(searchTerm) ||
        order.tracking_number?.toLowerCase().includes(searchTerm)
      );
    }
    return true;
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Container>
      <Content>
        <Header>
          <Title>My Orders</Title>
          <Subtitle>View your order history and meal deliveries</Subtitle>

          <FiltersContainer>
            <FilterGroup>
              <FilterLabel htmlFor="status-filter">
                <FiFilter style={{ marginRight: "0.5rem" }} />
                Status:
              </FilterLabel>
              <FilterSelect
                id="status-filter"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Orders</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="preparing">Preparing</option>
                <option value="out_for_delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <form
                onSubmit={handleSearch}
                style={{ display: "flex", gap: "0.5rem" }}
              >
                <SearchInput
                  type="text"
                  placeholder="Search orders..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                />
                <RefreshButton type="submit">
                  <FiSearch />
                </RefreshButton>
              </form>
            </FilterGroup>

            <RefreshButton onClick={fetchOrders} disabled={loading}>
              <FiRefreshCw className={loading ? "spinning" : ""} />
              Refresh
            </RefreshButton>
          </FiltersContainer>
        </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "4rem",
            }}
          >
            <LoadingSpinner />
          </div>
        ) : filteredOrders.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>No orders found</EmptyTitle>
            <EmptyText>
              {filters.status || filters.search
                ? "No orders match your current filters. Try adjusting your search criteria."
                : "You haven't placed any orders yet. Browse our meal plans to get started!"}
            </EmptyText>
          </EmptyState>
        ) : (
          <>
            <OrdersGrid>
              {filteredOrders.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <OrderCard order={order} />
                </motion.div>
              ))}
            </OrdersGrid>

            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                >
                  Previous
                </PageButton>

                {[...Array(totalPages)].map((_, i) => (
                  <PageButton
                    key={i + 1}
                    active={pagination.page === i + 1}
                    onClick={() =>
                      setPagination((prev) => ({ ...prev, page: i + 1 }))
                    }
                  >
                    {i + 1}
                  </PageButton>
                ))}

                <PageButton
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page === totalPages}
                >
                  Next
                </PageButton>
              </Pagination>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default Orders;
